import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Layers, Plus, Trash2, Loader2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CrearLotesMasivo({ 
  open, 
  onOpenChange, 
  fraccionamiento,
  onSuccess 
}) {
  const [loading, setLoading] = useState(false);
  const [manzana, setManzana] = useState('');
  const [moneda, setMoneda] = useState('USD');
  const [secciones, setSecciones] = useState([
    { desde: '0', hasta: '4', superficie: '', precio_m2: '', medidas: '' }
  ]);

  // Inicializar con valores del fraccionamiento
  useEffect(() => {
    if (fraccionamiento && open) {
      setMoneda(fraccionamiento.moneda_precio_m2 || 'USD');
      setSecciones([{ 
        desde: '0', 
        hasta: '4', 
        superficie: '', 
        precio_m2: fraccionamiento.precio_m2 ? String(fraccionamiento.precio_m2) : '',
        medidas: ''
      }]);
      setManzana('');
    }
  }, [fraccionamiento, open]);

  const agregarSeccion = () => {
    const ultimaSeccion = secciones[secciones.length - 1];
    const nuevoDesde = ultimaSeccion ? parseInt(ultimaSeccion.hasta || 0) : 0;
    setSecciones([...secciones, { 
      desde: String(nuevoDesde), 
      hasta: String(nuevoDesde + 5), 
      superficie: '', 
      precio_m2: ultimaSeccion?.precio_m2 || '',
      medidas: ''
    }]);
  };

  const eliminarSeccion = (index) => {
    if (secciones.length > 1) {
      setSecciones(secciones.filter((_, i) => i !== index));
    }
  };

  const actualizarSeccion = (index, campo, valor) => {
    const nuevasSecciones = [...secciones];
    nuevasSecciones[index] = { ...nuevasSecciones[index], [campo]: valor };
    setSecciones(nuevasSecciones);
  };

  // Calcular totales (desde no se incluye, solo hasta)
  const calcularTotales = () => {
    let totalLotes = 0;
    let totalPrecio = 0;
    
    secciones.forEach(s => {
      const desde = parseInt(s.desde) || 0;
      const hasta = parseInt(s.hasta) || 0;
      const superficie = parseFloat(s.superficie) || 0;
      const precioM2 = parseFloat(s.precio_m2) || 0;
      const cantidad = hasta > desde ? hasta - desde : 0;
      totalLotes += cantidad;
      if (superficie > 0 && precioM2 > 0) {
        totalPrecio += cantidad * superficie * precioM2;
      }
    });
    
    return { totalLotes, totalPrecio };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!manzana.trim()) {
      toast.error('Debes especificar la manzana');
      return;
    }
    
    // Validar secciones
    const seccionesValidas = secciones.filter(s => {
      const desde = parseInt(s.desde);
      const hasta = parseInt(s.hasta);
      const superficie = parseFloat(s.superficie);
      return desde >= 0 && hasta > desde && superficie > 0;
    });
    
    if (seccionesValidas.length === 0) {
      toast.error('Agrega al menos una sección válida con superficie');
      return;
    }
    
    setLoading(true);
    try {
      const seccionesData = seccionesValidas.map(s => ({
        desde: parseInt(s.desde),
        hasta: parseInt(s.hasta),
        superficie: parseFloat(s.superficie),
        medidas: s.medidas || `${s.superficie} m²`,
        precio_base: parseFloat(s.superficie) * (parseFloat(s.precio_m2) || 0)
      }));
      
      const response = await fetch(`${BACKEND_URL}/api/lotes/generar-secciones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fraccionamiento_id: fraccionamiento.fraccionamiento_id,
          manzana: manzana.trim(),
          moneda: moneda,
          secciones: seccionesData
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Error al crear lotes');
      }
      
      toast.success(`${result.lotes_creados} lotes creados en Mz. ${manzana}`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.message || 'Error al crear lotes');
    } finally {
      setLoading(false);
    }
  };

  const simboloMoneda = moneda === 'MXN' ? 'MXN $' : 'USD $';
  const { totalLotes, totalPrecio } = calcularTotales();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Layers className="w-5 h-5 text-amber-500" />
            Crear Lotes Masivamente
          </DialogTitle>
          {fraccionamiento && (
            <DialogDescription className="text-zinc-400">
              {fraccionamiento.nombre}
            </DialogDescription>
          )}
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Manzana y Moneda */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400">Manzana *</Label>
              <Input 
                value={manzana} 
                onChange={(e) => setManzana(e.target.value)} 
                placeholder="A, B, 1, 2..." 
                required
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Moneda</Label>
              <Select value={moneda} onValueChange={setMoneda}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="USD" className="text-white hover:bg-zinc-700">USD (Dólares)</SelectItem>
                  <SelectItem value="MXN" className="text-white hover:bg-zinc-700">MXN (Pesos)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Secciones */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-400">Secciones de Lotes</Label>
              <Button 
                type="button" 
                size="sm" 
                onClick={agregarSeccion}
                className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" /> Agregar
              </Button>
            </div>
            
            {/* Header */}
            <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_40px] gap-2 px-3 text-xs text-zinc-500">
              <span>Desde</span>
              <span>Hasta</span>
              <span>m²</span>
              <span>Medidas</span>
              <span>$/m²</span>
              <span></span>
            </div>
            
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {secciones.map((seccion, index) => {
                const superficie = parseFloat(seccion.superficie) || 0;
                const precioM2 = parseFloat(seccion.precio_m2) || 0;
                const precioLote = superficie * precioM2;
                
                return (
                  <div key={index} className="p-2 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_40px] gap-2 items-center">
                      <Input 
                        type="number" 
                        value={seccion.desde} 
                        onChange={(e) => actualizarSeccion(index, 'desde', e.target.value)} 
                        className="h-9 text-sm bg-zinc-800 border-zinc-600 text-white"
                        min="0"
                        placeholder="0"
                      />
                      <Input 
                        type="number" 
                        value={seccion.hasta} 
                        onChange={(e) => actualizarSeccion(index, 'hasta', e.target.value)} 
                        className="h-9 text-sm bg-zinc-800 border-zinc-600 text-white"
                        min="0"
                        placeholder="5"
                      />
                      <Input 
                        type="number" 
                        step="0.01"
                        value={seccion.superficie} 
                        onChange={(e) => actualizarSeccion(index, 'superficie', e.target.value)} 
                        placeholder="120"
                        className="h-9 text-sm bg-zinc-800 border-zinc-600 text-white"
                      />
                      <Input 
                        value={seccion.medidas} 
                        onChange={(e) => actualizarSeccion(index, 'medidas', e.target.value)} 
                        placeholder="10x12"
                        className="h-9 text-sm bg-zinc-800 border-zinc-600 text-white"
                      />
                      <Input 
                        type="number" 
                        step="0.01"
                        value={seccion.precio_m2} 
                        onChange={(e) => actualizarSeccion(index, 'precio_m2', e.target.value)} 
                        placeholder="850"
                        className="h-9 text-sm bg-zinc-800 border-zinc-600 text-white"
                      />
                      {secciones.length > 1 ? (
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="ghost"
                          onClick={() => eliminarSeccion(index)}
                          className="h-9 w-9 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : <div className="w-9" />}
                    </div>
                    {/* Precio calculado por lote */}
                    {precioLote > 0 && (
                      <div className="mt-1 text-xs text-emerald-400 text-right pr-12">
                        = {simboloMoneda}{precioLote.toLocaleString()} /lote
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Resumen */}
          <div className="p-3 bg-zinc-800 border border-zinc-700 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Total de lotes:</span>
              <span className="text-white font-bold">{totalLotes}</span>
            </div>
            {totalPrecio > 0 && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-zinc-400">Valor total:</span>
                <span className="text-emerald-400 font-bold flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  {simboloMoneda}{totalPrecio.toLocaleString()}
                </span>
              </div>
            )}
          </div>
          
          {/* Botones */}
          <div className="flex gap-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)} 
              className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={loading || totalLotes === 0} 
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Layers className="w-4 h-4 mr-2" />
              )}
              Crear {totalLotes} Lotes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
